export const CREDIT_NOTE_TYPES = [
    { value: 'sales_return', label: 'Product Sales Return' },
    { value: 'rate_difference', label: 'Rate Difference' },
    { value: 'discount', label: 'Discount' },
    { value: 'others', label: 'Others' },
];

export const CREDIT_NOTE_VOUCHER_MODES = [
    { value: 'Item Invoice', label: 'Item Invoice' },
    { value: 'Accounting Invoice', label: 'Accounting Invoice' },
];

export function getCreditNoteTypeLabel(cnType) {
    return CREDIT_NOTE_TYPES.find((item) => item.value === cnType)?.label || cnType || '';
}

export function recalculateCreditNoteLine(line, taxMeta = {}, changedField = null) {
    const qty = parseFloat(line.qty) || 0;
    const basicRate = parseFloat(line.basic_rate) || 0;
    const taxableAmt = changedField === 'taxable_amt'
        ? Math.round((parseFloat(line.taxable_amt) || 0) * 100) / 100
        : Math.round((qty * basicRate) * 100) / 100;
    const cgstPer = parseFloat(taxMeta.cgst_per ?? line.cgst_per) || 0;
    const sgstPer = parseFloat(taxMeta.sgst_per ?? line.sgst_per) || 0;
    const igstPer = parseFloat(taxMeta.igst_per ?? line.igst_per) || 0;
    let cgstAmt = 0;
    let sgstAmt = 0;
    let igstAmt = 0;
    if (igstPer > 0) {
        igstAmt = Math.round((taxableAmt * igstPer) / 100 * 100) / 100;
    } else {
        cgstAmt = Math.round((taxableAmt * cgstPer) / 100 * 100) / 100;
        sgstAmt = Math.round((taxableAmt * sgstPer) / 100 * 100) / 100;
    }
    const gstAmt = Math.round((cgstAmt + sgstAmt + igstAmt) * 100) / 100;
    const grossAmt = Math.round((taxableAmt + gstAmt) * 100) / 100;
    return {
        ...line,
        qty,
        basic_rate: basicRate,
        taxable_amt: taxableAmt,
        cgst_per: cgstPer,
        sgst_per: sgstPer,
        igst_per: igstPer,
        cgst_amt: cgstAmt,
        sgst_amt: sgstAmt,
        igst_amt: igstAmt,
        gst_amt: gstAmt,
        gross_amt: grossAmt,
    };
}

export function buildCreditNoteTotals(productLines = [], invoiceLines = []) {
    const totals = productLines.reduce((acc, line) => {
        acc.taxable_amt += parseFloat(line.taxable_amt) || 0;
        acc.cgst_amt += parseFloat(line.cgst_amt) || 0;
        acc.sgst_amt += parseFloat(line.sgst_amt) || 0;
        acc.igst_amt += parseFloat(line.igst_amt) || 0;
        acc.gst_amt += parseFloat(line.gst_amt) || 0;
        acc.gross_amt += parseFloat(line.gross_amt) || 0;
        return acc;
    }, {
        taxable_amt: 0,
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0,
        gst_amt: 0,
        gross_amt: 0,
        credit_amt: 0,
        round_off: 0,
    });
    totals.credit_amt = invoiceLines.reduce(
        (sum, line) => sum + (parseFloat(line.credit_amt) || 0),
        0,
    );
    totals.taxable_amt = Math.round(totals.taxable_amt * 100) / 100;
    totals.cgst_amt = Math.round(totals.cgst_amt * 100) / 100;
    totals.sgst_amt = Math.round(totals.sgst_amt * 100) / 100;
    totals.igst_amt = Math.round(totals.igst_amt * 100) / 100;
    totals.gst_amt = Math.round(totals.gst_amt * 100) / 100;
    totals.gross_amt = Math.round(totals.gross_amt * 100) / 100;
    totals.credit_amt = Math.round(totals.credit_amt * 100) / 100;
    const decimal = totals.credit_amt - Math.floor(totals.credit_amt);
    totals.round_off = decimal >= 0.5
        ? Math.round((1 - decimal) * 100) / 100
        : Math.round(-decimal * 100) / 100;
    totals.credit_amt = Math.round((totals.credit_amt + totals.round_off) * 100) / 100;
    return totals;
}

export function syncInvoiceCreditAmounts(cnType, productLines = [], invoiceLines = []) {
    const gross = Math.round(
        productLines.reduce((sum, line) => sum + (parseFloat(line.gross_amt) || 0), 0) * 100,
    ) / 100;
    if (!invoiceLines.length) {
        return invoiceLines;
    }
    if (cnType === 'discount') {
        return invoiceLines.map((line) => ({
            ...line,
            credit_amt: Math.round(
                Math.min(parseFloat(line.balance_amt) || 0, gross) * 100,
            ) / 100,
        }));
    }
    let remaining = gross;
    return invoiceLines.map((line) => {
        const balance = parseFloat(line.balance_amt) || 0;
        const creditAmt = Math.min(balance, Math.max(remaining, 0));
        remaining = Math.round((remaining - creditAmt) * 100) / 100;
        return { ...line, credit_amt: Math.round(creditAmt * 100) / 100 };
    });
}

export function applyCreditNoteTypeToLines(cnType, productLines = [], invoiceLines = []) {
    let nextProductLines = productLines;
    if (cnType === 'others' && productLines.length === 0) {
        nextProductLines = [{
            sl_no: 1,
            description: '',
            unit: '',
            qty: 0,
            basic_rate: 0,
            taxable_amt: 0,
            tax_id: '',
            cgst_per: 0,
            sgst_per: 0,
            igst_per: 0,
            cgst_amt: 0,
            sgst_amt: 0,
            igst_amt: 0,
            gst_amt: 0,
            gross_amt: 0,
        }];
    }
    return {
        productLines: nextProductLines,
        invoiceLines: syncInvoiceCreditAmounts(cnType, nextProductLines, invoiceLines),
    };
}
